import React, { useState, useEffect } from 'react';
import AdminNavigation from '../../components/ui/AdminNavigation';
import { barrelService } from '../../services/barrelService';
import { cartonService } from '../../services/cartonService';
import { BarChart3, RefreshCw, AlertTriangle, X, Wine, Package, TrendingUp, DollarSign, ShoppingCart } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, Cell, ResponsiveContainer, LineChart, Line
} from 'recharts';

const COLORS = ['#f59e0b', '#3b82f6', '#10b981', '#8b5cf6', '#ef4444', '#06b6d4'];

export default function UnifiedStockAnalytics() {
  const [barrelOrders, setBarrelOrders] = useState([]);
  const [barrelInventory, setBarrelInventory] = useState([]);
  const [cartonSales, setCartonSales] = useState([]);
  const [cartonInventory, setCartonInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [timeRange, setTimeRange] = useState('30days');

  const fetchData = async () => {
    setLoading(true);
    try {
      const [bOrders, bInv, cSales, cInv] = await Promise.all([
        barrelService?.getBarrelOrders(),
        barrelService?.getInventory(),
        cartonService?.getCartonSales(),
        cartonService?.getCartonInventory()
      ]);
      setBarrelOrders(bOrders || []);
      setBarrelInventory(bInv || []);
      setCartonSales(cSales || []);
      setCartonInventory(cInv || []);
    } catch (e) {
      setError('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const getDaysFromRange = () => {
    const map = { '7days': 7, '30days': 30, '90days': 90, '1year': 365 };
    return map?.[timeRange] || 30;
  };

  const filterByDate = (items, dateField = 'created_at') => {
    const days = getDaysFromRange();
    const cutoff = new Date(Date.now() - days * 24 * 60 * 60 * 1000);
    return items?.filter(i => new Date(i?.[dateField] || i?.created_at) >= cutoff);
  };

  const filteredBarrelOrders = filterByDate(barrelOrders);
  const filteredCartonSales = filterByDate(cartonSales, 'sale_date');

  // KPIs
  const barrelRevenue = filteredBarrelOrders?.filter(o => o?.paymentStatus === 'paid' || o?.payment_status === 'paid')
    ?.reduce((sum, o) => sum + (o?.totalPrice || o?.total_price || 0), 0);
  const cartonRevenue = filteredCartonSales?.filter(s => s?.paymentStatus === 'paid')
    ?.reduce((sum, s) => sum + (s?.totalPrice || 0), 0);
  const totalRevenue = barrelRevenue + cartonRevenue;

  const barrelQtySold = filteredBarrelOrders?.reduce((sum, o) => sum + (o?.quantity || 0), 0);
  const cartonQtySold = filteredCartonSales?.reduce((sum, s) => sum + (s?.quantity || 0), 0);

  // Stock distribution pie data
  const stockPieData = [
    ...barrelInventory?.map(i => ({
      name: i?.barrelType === 'noir_270l' ? 'Barrique Noire 270L' : 'Barrique Bleue 220L',
      value: i?.stockQuantity || i?.stock_quantity || 0,
      category: 'barrel'
    })),
    ...cartonInventory?.map(i => ({
      name: i?.cartonType,
      value: i?.stockQuantity || 0,
      category: 'carton'
    }))
  ]?.filter(d => d?.value > 0);

  // Sales by type bar data
  const salesByTypeData = [
    { name: 'Barrique Noire', qty: filteredBarrelOrders?.filter(o => (o?.barrelType || o?.barrel_type) === 'noir_270l')?.reduce((s, o) => s + (o?.quantity || 0), 0), revenue: filteredBarrelOrders?.filter(o => (o?.barrelType || o?.barrel_type) === 'noir_270l')?.reduce((s, o) => s + (o?.totalPrice || o?.total_price || 0), 0) },
    { name: 'Barrique Bleue', qty: filteredBarrelOrders?.filter(o => (o?.barrelType || o?.barrel_type) === 'bleu_220l')?.reduce((s, o) => s + (o?.quantity || 0), 0), revenue: filteredBarrelOrders?.filter(o => (o?.barrelType || o?.barrel_type) === 'bleu_220l')?.reduce((s, o) => s + (o?.totalPrice || o?.total_price || 0), 0) },
    ...cartonInventory?.map(ci => ({
      name: ci?.cartonType,
      qty: filteredCartonSales?.filter(s => s?.cartonType === ci?.cartonType)?.reduce((s, sale) => s + (sale?.quantity || 0), 0),
      revenue: filteredCartonSales?.filter(s => s?.cartonType === ci?.cartonType)?.reduce((s, sale) => s + (sale?.totalPrice || 0), 0)
    }))
  ]?.filter(d => d?.qty > 0 || d?.revenue > 0);

  // Monthly trend (last 6 months)
  const monthlyTrend = (() => {
    const months = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date();
      d?.setMonth(d?.getMonth() - i);
      const label = d?.toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' });
      const monthStart = new Date(d.getFullYear(), d.getMonth(), 1);
      const monthEnd = new Date(d.getFullYear(), d.getMonth() + 1, 0);
      const bRev = barrelOrders?.filter(o => {
        const dt = new Date(o?.created_at);
        return dt >= monthStart && dt <= monthEnd && (o?.paymentStatus === 'paid' || o?.payment_status === 'paid');
      })?.reduce((s, o) => s + (o?.totalPrice || o?.total_price || 0), 0);
      const cRev = cartonSales?.filter(s => {
        const dt = new Date(s?.created_at || s?.sale_date);
        return dt >= monthStart && dt <= monthEnd && s?.paymentStatus === 'paid';
      })?.reduce((s, sale) => s + (sale?.totalPrice || 0), 0);
      months?.push({ month: label, barriques: Math.round(bRev), cartons: Math.round(cRev) });
    }
    return months;
  })();

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
      <AdminNavigation />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8 gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <BarChart3 className="h-7 w-7 text-green-600" />
              Analytiques Stock — Barriques & Cartons
            </h1>
            <p className="text-gray-500 dark:text-gray-400 mt-1">Analyse unifiée des performances de vente et des stocks</p>
          </div>
          <div className="flex gap-2">
            <select value={timeRange} onChange={e => setTimeRange(e?.target?.value)}
              className="border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-800 text-gray-900 dark:text-white">
              <option value="7days">7 derniers jours</option>
              <option value="30days">30 derniers jours</option>
              <option value="90days">90 derniers jours</option>
              <option value="1year">12 derniers mois</option>
            </select>
            <button onClick={fetchData} className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 dark:border-slate-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700">
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" /> {error}
            <button onClick={() => setError('')} className="ml-auto"><X className="h-4 w-4" /></button>
          </div>
        )}

        {loading ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">Chargement...</div>
        ) : (
          <>
            {/* KPIs */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
              <div className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-gray-200 dark:border-slate-700">
                <div className="flex items-center gap-2 mb-1">
                  <DollarSign className="h-4 w-4 text-green-600" />
                  <p className="text-xs text-gray-500 dark:text-gray-400">CA Total</p>
                </div>
                <p className="text-xl font-bold text-green-600">{totalRevenue?.toFixed(2)} €</p>
              </div>
              <div className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-gray-200 dark:border-slate-700">
                <div className="flex items-center gap-2 mb-1">
                  <Wine className="h-4 w-4 text-amber-600" />
                  <p className="text-xs text-gray-500 dark:text-gray-400">CA Barriques</p>
                </div>
                <p className="text-xl font-bold text-amber-600">{barrelRevenue?.toFixed(2)} €</p>
              </div>
              <div className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-gray-200 dark:border-slate-700">
                <div className="flex items-center gap-2 mb-1">
                  <Package className="h-4 w-4 text-blue-600" />
                  <p className="text-xs text-gray-500 dark:text-gray-400">CA Cartons</p>
                </div>
                <p className="text-xl font-bold text-blue-600">{cartonRevenue?.toFixed(2)} €</p>
              </div>
              <div className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-gray-200 dark:border-slate-700">
                <div className="flex items-center gap-2 mb-1">
                  <ShoppingCart className="h-4 w-4 text-purple-600" />
                  <p className="text-xs text-gray-500 dark:text-gray-400">Unités vendues</p>
                </div>
                <p className="text-xl font-bold text-purple-600">{barrelQtySold + cartonQtySold}</p>
              </div>
            </div>

            {/* Charts row 1 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* Monthly trend */}
              <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 p-4">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-green-600" />
                  Évolution mensuelle du CA (€)
                </h3>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={monthlyTrend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v) => `${v} €`} />
                    <Legend />
                    <Line type="monotone" dataKey="barriques" stroke="#f59e0b" strokeWidth={2} name="Barriques" dot={false} />
                    <Line type="monotone" dataKey="cartons" stroke="#3b82f6" strokeWidth={2} name="Cartons" dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Stock distribution */}
              <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 p-4">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center gap-2">
                  <BarChart3 className="h-4 w-4 text-blue-600" />
                  Répartition des stocks actuels
                </h3>
                {stockPieData?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie data={stockPieData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, value }) => `${name}: ${value}`} labelLine={false}>
                        {stockPieData?.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS?.[index % COLORS?.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[220px] flex items-center justify-center text-gray-400 text-sm">Aucun stock disponible</div>
                )}
              </div>
            </div>

            {/* Sales by type */}
            {salesByTypeData?.length > 0 && (
              <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 p-4">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center gap-2">
                  <ShoppingCart className="h-4 w-4 text-purple-600" />
                  Ventes par type de produit (période sélectionnée)
                </h3>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={salesByTypeData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend />
                    <Bar yAxisId="left" dataKey="qty" fill="#8b5cf6" name="Quantité vendue" radius={[4, 4, 0, 0]} />
                    <Bar yAxisId="right" dataKey="revenue" fill="#10b981" name="CA (€)" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
